import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Chip } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import 'moment/locale/es';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: '12px',
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  fecha: {
    width: '150px',
    // En celular las dos fechas comparten la fila
    [theme.breakpoints.down('xs')]: {
      width: 'calc(50% - 6px)'
    }
  },
  atajos: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    paddingBottom: '4px'
  }
}));

/** Atajos: cada uno arma { fechaDesde, fechaHasta } a partir de hoy. */
export const atajosRangoFechas = [
  { nombre: 'Hoy', rango: () => ({ fechaDesde: moment().startOf('day'), fechaHasta: moment().startOf('day') }) },
  { nombre: 'Últimos 7 días', rango: () => ({ fechaDesde: moment().subtract(6, 'days').startOf('day'), fechaHasta: moment().startOf('day') }) },
  { nombre: 'Este mes', rango: () => ({ fechaDesde: moment().startOf('month'), fechaHasta: moment().startOf('day') }) }
];

/** Las dos fechas son válidas y Hasta no es anterior a Desde. */
export const rangoFechasValido = ({ fechaDesde, fechaHasta }) =>
  !!fechaDesde && !!fechaHasta && fechaDesde.isValid() && fechaHasta.isValid() && !fechaHasta.isBefore(fechaDesde, 'day');

const mismoRango = (a, b) => rangoFechasValido(a) && a.fechaDesde.isSame(b.fechaDesde, 'day') && a.fechaHasta.isSame(b.fechaHasta, 'day');

/**
 * Rango de fechas con Desde y Hasta siempre visibles, y atajos (Hoy, Últimos 7 días, Este mes) que completan las dos
 * fechas; el atajo que coincide con el rango elegido queda marcado. onChange({ fechaDesde, fechaHasta }) con moment.
 */
const RangoFechasSelector = ({ fechaDesde, fechaHasta, onChange, disabled }) => {
  const classes = useStyles();
  const rango = { fechaDesde, fechaHasta };
  const hastaAnterior =
    !!fechaDesde && !!fechaHasta && fechaDesde.isValid() && fechaHasta.isValid() && fechaHasta.isBefore(fechaDesde, 'day');

  const picker = (label, campo, extra) => (
    <KeyboardDatePicker
      className={classes.fecha}
      label={label}
      format="DD/MM/YYYY"
      autoOk
      variant="inline"
      value={rango[campo]}
      onChange={(fecha) => onChange({ ...rango, [campo]: fecha })}
      invalidDateMessage="Fecha inválida"
      disabled={disabled}
      {...extra}
    />
  );

  return (
    <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={'es'}>
      <div className={classes.root}>
        {picker('Desde', 'fechaDesde')}
        {picker('Hasta', 'fechaHasta', { error: hastaAnterior || undefined, helperText: hastaAnterior ? 'Es anterior a Desde' : undefined })}
        <div className={classes.atajos}>
          {atajosRangoFechas.map((atajo) => {
            const elegido = mismoRango(rango, atajo.rango());
            return (
              <Chip
                key={atajo.nombre}
                label={atajo.nombre}
                size="small"
                clickable
                disabled={disabled}
                color={elegido ? 'primary' : 'default'}
                variant={elegido ? 'default' : 'outlined'}
                onClick={() => onChange(atajo.rango())}
              />
            );
          })}
        </div>
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default RangoFechasSelector;
